<template>
    <StandardLayout>
        <div class="admin-stations">
            <DoubleHeader :title="$tc('pageTitles.adminStations')" :backTitle="$t('admin.backBtn')" backRoute="adminMain" />

            <div class="search-bar">
                <div class="search-input-wrap">
                    <i class="icon icon-search search-icon" />
                    <input
                        type="text"
                        class="search-input"
                        v-model="query"
                        :placeholder="$tc('admin.stations.searchPlaceholder')"
                        @input="onQueryInput"
                        autocomplete="off"
                    />
                    <button v-if="query" class="search-clear" @click="clearSearch">
                        <i class="icon-close" />
                    </button>
                </div>
            </div>

            <div v-if="busy" class="admin-loading-container">
                <Spinner />
                {{ $tc("admin.loadingData") }}
            </div>

            <template v-if="!busy">
                <div class="card">
                    <div class="table-responsive">
                        <table class="stations-table">
                            <thead>
                                <tr>
                                    <th class="th-sortable" :class="sortClasses('id')" @click="sortColumn('id')">
                                        {{ $t("admin.stations.id") }}
                                        <span class="sort-indicator" />
                                    </th>
                                    <th class="th-sortable" :class="sortClasses('name')" @click="sortColumn('name')">
                                        {{ $t("admin.stations.name") }}
                                        <span class="sort-indicator" />
                                    </th>
                                    <th>{{ $t("admin.stations.deviceId") }}</th>
                                    <th class="th-sortable" :class="sortClasses('owner')" @click="sortColumn('owner')">
                                        {{ $t("admin.stations.owner") }}
                                        <span class="sort-indicator" />
                                    </th>
                                    <th class="th-sortable" :class="sortClasses('ownerEmail')" @click="sortColumn('ownerEmail')">
                                        {{ $t("admin.stations.ownerEmail") }}
                                        <span class="sort-indicator" />
                                    </th>
                                    <th class="th-sortable" :class="sortClasses('recording')" @click="sortColumn('recording')">
                                        {{ $t("admin.stations.recording") }}
                                        <span class="sort-indicator" />
                                    </th>
                                    <th class="th-sortable" :class="sortClasses('lastIngestion')" @click="sortColumn('lastIngestion')">
                                        {{ $t("admin.stations.lastIngestion") }}
                                        <span class="sort-indicator" />
                                    </th>
                                    <th class="th-sortable" :class="sortClasses('updated')" @click="sortColumn('updated')">
                                        {{ $t("admin.stations.updated") }}
                                        <span class="sort-indicator" />
                                    </th>
                                    <th class="th-sortable" :class="sortClasses('firmware')" @click="sortColumn('firmware')">
                                        {{ $t("admin.stations.firmware") }}
                                        <span class="sort-indicator" />
                                    </th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody v-for="station in stations" :key="station.id">
                                <tr :class="{ 'row-active': focused && focused.id === station.id }" @click="selected(station)">
                                    <td class="cell-id">{{ station.id }}</td>
                                    <td class="cell-name">{{ station.name }}</td>
                                    <td class="cell-device-id">
                                        <span class="device-id-text">{{ station.deviceId }}</span>
                                        <button
                                            class="copy-btn"
                                            @click.stop="copyDeviceId(station)"
                                            :title="$t('admin.stations.copyDeviceId')"
                                        >
                                            <i :class="copiedStationId === station.id ? 'icon-icon-tick' : 'icon-copy'"></i>
                                        </button>
                                    </td>
                                    <td>{{ station.owner.name }}</td>
                                    <td class="cell-email">{{ station.owner.email }}</td>
                                    <td class="cell-date">{{ station.recordingStartedAt | prettyTime }}</td>
                                    <td class="cell-date">{{ station.lastIngestionAt | prettyTime }}</td>
                                    <td class="cell-date">{{ station.updatedAt | prettyTime }}</td>
                                    <td class="cell-date">
                                        {{ station.firmwareNumber ? station.firmwareNumber : "-" }}
                                    </td>
                                    <td>
                                        <button class="action-btn action-btn-danger" @click.stop="deleteStation(station)">
                                            {{ $t("admin.stations.delete") }}
                                        </button>
                                    </td>
                                </tr>
                                <tr v-if="focused && focused.id === station.id" class="expanded-row">
                                    <td colspan="10" class="expanded-cell">
                                        <div class="expanded-content">
                                            <div class="expanded-col-uploads">
                                                <h4 class="expanded-title">{{ $t("admin.stations.uploads.heading") }}</h4>
                                                <div v-if="focused.uploads && focused.uploads.length === 0" class="section-empty">
                                                    {{ $tc("admin.stations.uploads.noUploads") }}
                                                </div>
                                                <table class="uploads-table" v-if="focused.uploads && focused.uploads.length > 0">
                                                    <thead>
                                                        <tr>
                                                            <th>{{ $t("admin.stations.uploads.id") }}</th>
                                                            <th>{{ $t("admin.stations.uploads.date") }}</th>
                                                            <th>{{ $t("admin.stations.uploads.type") }}</th>
                                                            <th>{{ $t("admin.stations.uploads.size") }}</th>
                                                            <th>{{ $t("admin.stations.uploads.blocks") }}</th>
                                                            <th>{{ $t("admin.stations.uploads.url") }}</th>
                                                            <th></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="upload in focused.uploads" :key="upload.id">
                                                            <td class="cell-id">{{ upload.id }}</td>
                                                            <td class="cell-date">{{ upload.time | prettyTime }}</td>
                                                            <td>{{ upload.type }}</td>
                                                            <td>{{ upload.size }}</td>
                                                            <td>{{ upload.blocks }}</td>
                                                            <td class="cell-url">
                                                                <a :href="upload.url" target="_blank" class="info-link">
                                                                    {{ upload.url }}
                                                                    <i class="icon-external-link"></i>
                                                                </a>
                                                            </td>
                                                            <td>
                                                                <button class="action-btn" @click="onProcessUpload(upload)">
                                                                    {{ $t("admin.stations.uploads.process") }}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div class="expanded-col-sidebar">
                                                <div>
                                                    <h4 class="expanded-title">{{ $t("admin.stations.information") }}</h4>
                                                    <div class="info-grid" v-if="focused.data">
                                                        <div class="info-row">
                                                            <span class="info-label">{{ $t("admin.stations.data") }}</span>
                                                            <span class="info-value">
                                                                {{ focused.data.start | prettyTime }} – {{ focused.data.end | prettyTime }}
                                                            </span>
                                                        </div>
                                                        <div class="info-row">
                                                            <span class="info-value">
                                                                {{ focused.data.numberOfSamples }} {{ $t("admin.stations.samples") }}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div class="info-row" v-if="station.location && station.location.precise">
                                                        <span class="info-label">{{ $t("admin.stations.location") }}:</span>
                                                        <a class="info-link" :href="urlForMaps(station)" target="_blank">
                                                            {{ station.location.precise[1] | prettyCoordinate }},
                                                            {{ station.location.precise[0] | prettyCoordinate }}
                                                            <i class="icon-external-link"></i>
                                                        </a>
                                                    </div>
                                                    <div class="info-row">
                                                        <span class="info-label">{{ $t("admin.stations.serverLogs") }}:</span>
                                                        <a class="info-link" :href="urlForServerLogs(station)" target="_blank">
                                                            {{ $t("admin.stations.10days") }}
                                                            <i class="icon-external-link"></i>
                                                        </a>
                                                    </div>
                                                    <div class="expanded-actions">
                                                        <button class="action-btn" @click="onProcessData(station)">
                                                            {{ $t("admin.stations.processData") }}
                                                        </button>
                                                        <button class="action-btn" @click="onExplore(station)">
                                                            {{ $t("admin.stations.exploreData") }}
                                                        </button>
                                                    </div>
                                                </div>
                                                <div>
                                                    <h4 class="expanded-title">{{ $t("admin.stations.notes") }}</h4>
                                                    <div class="notes-section">
                                                        <textarea
                                                            class="notes-textarea"
                                                            v-model="editingNotes"
                                                            :placeholder="$tc('admin.stations.notesPlaceholder')"
                                                            rows="3"
                                                        />
                                                        <div class="notes-actions">
                                                            <button
                                                                class="action-btn"
                                                                :disabled="!notesChanged || savingNotes"
                                                                @click="saveNotes(station)"
                                                            >
                                                                {{ $tc("admin.stations.notesSave") }}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div>
                                                    <h4 class="expanded-title">{{ $t("admin.stations.projects") }}</h4>
                                                    <div v-if="!stationProjects || stationProjects.length === 0" class="section-empty">
                                                        {{ $t("admin.stations.noProjects") }}
                                                    </div>
                                                    <div v-else class="info-grid">
                                                        <router-link
                                                            v-for="project in stationProjects"
                                                            :key="project.id"
                                                            :to="{ name: 'viewProject', params: { id: project.id } }"
                                                            target="_blank"
                                                            class="info-row info-row-link"
                                                        >
                                                            <span class="info-label">{{ project.id }}</span>
                                                            <span class="info-value">{{ project.name }}</span>
                                                        </router-link>
                                                    </div>
                                                </div>
                                                <div>
                                                    <h4 class="expanded-title">{{ $t("admin.stations.transfer") }}</h4>
                                                    <div class="info-row">
                                                        <span class="info-label">{{ $t("admin.stations.transferOwnerLabel") }}</span>
                                                        <span class="info-value">{{ station.owner.name }} ({{ station.owner.email }})</span>
                                                    </div>
                                                    <TransferStation
                                                        :station="station"
                                                        @transferred="(user) => onTransferred(station, user)"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="pagination-container" v-if="totalPages > 1">
                    <PaginationControls :page="page" :totalPages="totalPages" @new-page="onNewPage" />
                </div>
            </template>
        </div>
    </StandardLayout>
</template>

<script lang="ts">
import Vue from "vue";
import StandardLayout from "../StandardLayout.vue";
import CommonComponents from "@/views/shared";
import PaginationControls from "@/views/shared/PaginationControls.vue";
import { Station, SimpleUser, EssentialStation, Project } from "@/api/api";
import { ActionTypes } from "@/store";
import { SnackbarStyle } from "@/store/modules/snackbar";
import { BookmarkFactory, serializeBookmark } from "@/views/viz/viz";
import TransferStation from "./TransferStation.vue";
import { Buffer } from "buffer";

export default Vue.extend({
    name: "AdminStations",
    components: {
        ...CommonComponents,
        StandardLayout,
        PaginationControls,
        TransferStation,
    },
    props: {
        page: {
            type: Number,
            default: 0,
        },
        station: {
            type: Number,
            default: null,
        },
    },
    data(): {
        stations: EssentialStation[];
        pageSize: number;
        totalPages: number;
        focused: Station | null;
        busy: boolean;
        copiedStationId: number | null;
        query: string;
        searchTimer: number | null;
        sortBy: string;
        sortDir: "asc" | "desc";
        editingNotes: string;
        savingNotes: boolean;
    } {
        return {
            stations: [],
            pageSize: 20,
            totalPages: 0,
            focused: null,
            busy: false,
            copiedStationId: null,
            query: "",
            searchTimer: null,
            sortBy: "",
            sortDir: "asc",
            editingNotes: "",
            savingNotes: false,
        };
    },
    computed: {
        stationProjects(): Project[] {
            return this.$store.getters.stationProjects as Project[];
        },
        notesChanged(): boolean {
            const original = this.focused?.internalNotes ?? "";
            return this.editingNotes !== original;
        },
        sortClasses(): (col: string) => Record<string, boolean> {
            return (col: string) => ({
                "th-sort-active": this.sortBy === col,
                "th-sort-asc": this.sortBy === col && this.sortDir === "asc",
                "th-sort-desc": this.sortBy === col && this.sortDir === "desc",
            });
        },
    },
    mounted(this: any) {
        this.refresh();
        this.loadStation();
    },
    watch: {
        async page() {
            await this.refresh();
        },
        async station(val) {
            if (!val) {
                this.focused = null;
            } else {
                await this.loadStation();
            }
        },
    },
    methods: {
        async copyDeviceId(station: EssentialStation): Promise<void> {
            await navigator.clipboard.writeText(station.deviceId);
            this.copiedStationId = station.id;
            setTimeout(() => {
                this.copiedStationId = null;
            }, 1500);
        },
        async selected(station: EssentialStation): Promise<void> {
            if (this.focused && this.focused.id === station.id) {
                this.$router.push({ name: "adminStations", query: { page: `${this.page}` } }).catch(() => void 0);
            } else {
                this.$router.push({ name: "adminStations", query: { page: `${this.page}`, station: `${station.id}` } }).catch(() => void 0);
            }
        },
        async loadStation(): Promise<void> {
            if (!this.station || (this.focused && this.focused.id == this.station)) {
                return;
            }
            this.focused = null;
            await Promise.all([
                this.$services.api.getStation(this.station).then((station) => {
                    this.focused = station;
                    this.editingNotes = station.internalNotes ?? "";
                }),
                this.$store.dispatch(ActionTypes.NEED_PROJECTS_FOR_STATION, { id: this.station }),
            ]);
        },
        async refresh(): Promise<void> {
            this.busy = true;
            this.stations = [];
            await this.$services.api
                .getAllStations(
                    this.page,
                    this.pageSize,
                    this.query.trim() || undefined,
                    undefined,
                    this.sortBy || undefined,
                    this.sortBy ? this.sortDir : undefined
                )
                .then((page) => {
                    this.totalPages = Math.ceil(page.total / this.pageSize);
                    this.stations = page.stations;
                    this.busy = false;
                });
        },
        sortColumn(col: string): void {
            if (this.sortBy === col) {
                this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
            } else {
                this.sortBy = col;
                this.sortDir = "asc";
            }
            this.$router.replace({ name: "adminStations", query: { page: "0" } }).catch(() => void 0);
            this.refresh();
        },
        onQueryInput() {
            if (this.searchTimer) clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => {
                this.$router.replace({ name: "adminStations", query: { page: "0" } }).catch(() => void 0);
                this.refresh();
            }, 300);
        },
        clearSearch() {
            this.query = "";
            this.$router.replace({ name: "adminStations", query: { page: "0" } }).catch(() => void 0);
            this.refresh();
        },
        onNewPage(page: number) {
            window.scrollTo(0, 0);
            this.$router.push({
                name: "adminStations",
                query: { page: `${page}` },
            });
        },
        async deleteStation(station: EssentialStation): Promise<void> {
            await this.$confirm({
                message: this.$tc("generalDeleteConfirm"),
                button: {
                    no: this.$tc("no"),
                    yes: this.$tc("yes"),
                },
                callback: (confirm) => {
                    if (confirm) {
                        return this.$services.api.deleteStation(station.id).then(() => {
                            return this.refresh();
                        });
                    }
                },
            });
        },
        urlForMaps(station: EssentialStation): string {
            const precise = station.location?.precise;
            if (!precise) return "";
            const [lng, lat] = precise;
            return `https://www.google.com/maps?q=${lat},${lng}`;
        },
        urlForServerLogs(station: EssentialStation): string {
            const deviceIdBase64 = Buffer.from(station.deviceId, "hex").toString("base64");
            const deviceIdHex = station.deviceId;
            const query = `device_id:("${deviceIdBase64}" or "${deviceIdHex}")`;
            return "https://code.conservify.org/logs-viewer/?range=864000&query=" + encodeURIComponent(query);
        },
        async onProcessUpload(upload: { id: number }): Promise<void> {
            await this.$confirm({
                message: this.$tc("admin.stations.uploads.processUploadConfirm"),
                button: {
                    no: this.$tc("no"),
                    yes: this.$tc("yes"),
                },
                callback: async (confirm) => {
                    if (confirm) {
                        await this.$services.api.adminProcessUpload(upload.id);
                    }
                },
            });
        },
        async onProcessData(station: EssentialStation): Promise<void> {
            await this.$confirm({
                message: `Are you sure? This could take a while for certain stations.`,
                button: {
                    no: "No",
                    yes: "Yes",
                },
                callback: async (confirm) => {
                    if (confirm) {
                        await this.$services.api.adminProcessStationData(station.id);
                    }
                },
            });
        },
        async saveNotes(station: EssentialStation): Promise<void> {
            this.savingNotes = true;
            await this.$services.api
                .adminUpdateStationInternalNotes(station.id, this.editingNotes)
                .then(() => {
                    if (this.focused) {
                        this.focused.internalNotes = this.editingNotes;
                    }
                    this.$store.dispatch(ActionTypes.SHOW_SNACKBAR, {
                        message: this.$tc("admin.stations.notesSaved"),
                        type: SnackbarStyle.success,
                    });
                })
                .catch(() => {
                    this.$store.dispatch(ActionTypes.SHOW_SNACKBAR, {
                        message: this.$tc("admin.stations.notesSaveFailed"),
                        type: SnackbarStyle.fail,
                    });
                })
                .finally(() => {
                    this.savingNotes = false;
                });
        },
        onTransferred(station: EssentialStation, user: SimpleUser): void {
            station.owner = user;
        },
        onExplore(station: EssentialStation): void {
            const bm = BookmarkFactory.forStation(station.id);
            const route = this.$router.resolve({ name: "exploreBookmark", query: { bookmark: serializeBookmark(bm) } });
            window.open(route.href, "_blank");
        },
    },
});
</script>

<style scoped lang="scss">
@use "src/scss/variables";
@use "src/scss/mixins";

.admin-stations {
    display: flex;
    flex-direction: column;
    padding: 40px 40px;
    max-width: 1440px;
    text-align: left;

    @include mixins.bp-down(variables.$lg) {
        padding: 40px 60px;
    }

    @include mixins.bp-down(variables.$md) {
        padding: 30px 30px;
    }
}

.search-bar {
    margin-top: 24px;
    margin-bottom: 4px;
}

.search-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
    max-width: 330px;
}

.search-icon {
    position: absolute;
    left: 12px;
    pointer-events: none;

    &:before {
        font-size: 14px;
        color: var(--color-dark);
    }
}

.search-input {
    width: 100%;
    padding: 9px 12px 9px 36px;
    border: 1px solid var(--color-border);
    border-radius: 3px;
    font-size: 14px;
    font-family: var(--font-family);
    color: var(--color-dark);
    background: #fff;
    transition: border-color 0.15s ease;

    &:focus {
        outline: none;
        border-color: var(--color-primary);
    }

    &::placeholder {
        color: #9aa1ab;
    }
}

.search-clear {
    position: absolute;
    right: 10px;
    background: none;
    border: none;
    cursor: pointer;
    color: #9aa1ab;
    font-size: 12px;
    padding: 2px;
    display: flex;
    align-items: center;

    &:hover {
        color: var(--color-dark);
    }
}

.card {
    background: #fff;
    border: 1px solid var(--color-border);
    border-radius: 3px;
    margin-top: 24px;
    overflow: hidden;
}

.table-responsive {
    overflow-x: auto;
}

.stations-table {
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        padding: 10px 12px;
        text-align: left;
        font-size: 13px;
        white-space: nowrap;
    }

    th {
        font-family: var(--font-family-bold);
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #6a6d71;
        border-bottom: 1px solid var(--color-border);
    }

    th.th-sortable {
        cursor: pointer;
        user-select: none;
        white-space: nowrap;

        &:hover {
            color: var(--color-dark);
        }

        &.th-sort-active {
            color: var(--color-primary);
        }

        .sort-indicator {
            display: inline-block;
            width: 0;
            height: 0;
            margin-left: 1px;
            vertical-align: middle;
            opacity: 0.25;
            border-left: 4px solid transparent;
            border-right: 4px solid transparent;
            border-top: 5px solid currentColor;
        }

        &:hover .sort-indicator {
            opacity: 0.5;
        }

        &.th-sort-asc .sort-indicator {
            opacity: 1;
            border-top: none;
            border-bottom: 5px solid currentColor;
            margin-top: -1px;
        }

        &.th-sort-desc .sort-indicator {
            opacity: 1;
            border-top: 5px solid currentColor;
            border-bottom: none;
        }
    }

    td {
        border-bottom: 1px solid #f0f0f0;
    }

    tbody:last-child tr:last-child td {
        border-bottom: none;
    }

    tbody tr {
        cursor: pointer;
        transition: background 0.12s ease;

        &:hover {
            background: #f9fafb;
        }

        &.row-active {
            background: #f4f5f7;
        }
    }
}

.cell-name {
    font-family: var(--font-family-bold);
}

.cell-device-id {
    white-space: nowrap;

    &:hover .copy-btn {
        opacity: 1;
    }
}

.device-id-text {
    font-family: monospace;
    font-size: 12px;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
    cursor: default;
}

.copy-btn {
    opacity: 0;
    width: 22px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px 4px;
    margin-left: 4px;
    color: #6a6d71;
    font-size: 12px;
    border-radius: 3px;
    vertical-align: middle;
    transition: opacity 0.15s ease, color 0.15s ease, background 0.15s ease;

    &:hover {
        color: var(--color-primary);
        background: rgba(27, 128, 201, 0.08);
    }

    .icon-icon-tick:before {
        color: #3f8530;
    }
}

.cell-id {
    opacity: 0.5;
}

.cell-date {
    color: #6a6d71;
}

.cell-email {
    color: #6a6d71;
    font-size: 12px;
}

.cell-location {
    font-size: 12px;
    color: #6a6d71;
}

.cell-url {
    max-width: 200px;

    a {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.info-row-link {
    cursor: pointer;
    text-decoration: none;
    border-radius: 3px;
    transition: background 0.12s ease, color 0.12s ease;
    margin: 0;
    padding: 7px 5px;

    &:hover {
        background: rgba(27, 128, 201, 0.06);
        color: var(--color-primary);

        .info-label {
            color: var(--color-primary);
        }
    }

    .info-label {
        min-width: 22px;
    }
}

.action-btn {
    padding: 5px 14px;
    font-size: 12px;
    font-family: var(--font-family-medium);
    color: var(--color-primary);
    background: transparent;
    border: 1px solid var(--color-primary);
    border-radius: 3px;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
    white-space: nowrap;

    &:hover {
        background: var(--color-primary);
        color: #fff;
    }

    &:disabled {
        color: var(--color-border);
        border-color: var(--color-border);
        background: transparent;
        cursor: not-allowed;
    }
}

.action-btn-danger {
    color: var(--color-danger);
    border-color: var(--color-danger);

    &:hover {
        background: var(--color-danger);
        color: #fff;
    }
}

.expanded-row {
    cursor: default !important;

    &:hover {
        background: transparent !important;
    }
}

.expanded-cell {
    padding: 0 !important;
    border-bottom: 2px solid var(--color-border) !important;
}

.expanded-content {
    display: flex;
    gap: 32px;
    padding: 24px;
    background: #f9fafb;

    @include mixins.bp-down(variables.$md) {
        flex-direction: column;
    }
}

.expanded-col-uploads {
    flex: 0 1 900px;
    min-width: 0;
}

.expanded-col-sidebar {
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding-right: 120px;
}

.expanded-title {
    font-family: var(--font-family-bold);
    font-size: 14px;
    margin: 0 0 14px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--color-border);
}

.section-empty {
    font-size: 13px;
    color: #9aa1ab;
    padding: 8px 0;
}

.uploads-table {
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        padding: 6px 10px;
        text-align: left;
        font-size: 12px;
    }

    th {
        font-family: var(--font-family-bold);
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        color: #6a6d71;
        border-bottom: 1px solid var(--color-border);
    }

    td {
        border-bottom: 1px solid #f0f0f0;
    }

    tr:last-child td {
        border-bottom: none;
    }
}

.info-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.info-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
    font-size: 13px;
    margin-bottom: 6px;
}

.info-label {
    color: #6a6d71;
    white-space: nowrap;
}

.info-value {
    font-family: var(--font-family-medium);
}

.info-link {
    color: var(--color-primary);
    text-decoration: none;
    font-size: 13px;

    i {
        font-size: 10px;
        margin-left: 4px;
    }

    &:hover {
        text-decoration: underline;
    }
}

.notes-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.notes-textarea {
    padding: 8px 10px;
    border: 1px solid var(--color-border);
    border-radius: 3px;
    font-size: 13px;
    font-family: var(--font-family);
    color: var(--color-dark);
    background: #fff;
    resize: vertical;
    min-height: 60px;
    transition: border-color 0.15s ease;

    &:focus {
        outline: none;
        border-color: var(--color-primary);
    }

    &::placeholder {
        color: #9aa1ab;
    }
}

.notes-actions {
    display: flex;
    justify-content: flex-end;
}

.expanded-actions {
    display: flex;
    gap: 8px;
    margin-top: 14px;
}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: center;
}

::v-deep .user-picker {
    margin-bottom: 7px;
}
</style>
